/**
 * Canonical option lists for the post-job flow and job-label formatting
 * (lib/company-jobs.ts). Values are the ids the API stores; labels resolve
 * through the common namespace.
 *
 * The Phase 1 employer launch removed the legacy nationality/gender/
 * gender-reason/prescreen lists (protected attributes, PRD §7.4) together
 * with the old 3-step draft model — the four-step state now lives in
 * ./post-job/post-job-machine.ts.
 */
export interface OptionItem {
  value: string;
  labelKey: string;
}

/** A country option plus the ISO 3166-1 alpha-2 code the curated datasets key on. */
export interface CountryOptionItem extends OptionItem {
  /** `null` for "Remote", which is a work arrangement rather than a place. */
  isoCode: string | null;
}

/** A city option tagged with the country option it belongs to. */
export interface CityOptionItem extends OptionItem {
  country: string;
}

export const DEPARTMENT_OPTIONS: OptionItem[] = [
  { value: 'engineering', labelKey: 'jobPost.options.departments.engineering' },
  { value: 'marketing', labelKey: 'jobPost.options.departments.marketing' },
  { value: 'sales', labelKey: 'jobPost.options.departments.sales' },
  { value: 'product', labelKey: 'jobPost.options.departments.product' },
  { value: 'design', labelKey: 'jobPost.options.departments.design' },
  { value: 'finance', labelKey: 'jobPost.options.departments.finance' },
  { value: 'operations', labelKey: 'jobPost.options.departments.operations' },
  { value: 'hr', labelKey: 'jobPost.options.departments.hr' },
  { value: 'legal', labelKey: 'jobPost.options.departments.legal' },
  { value: 'customer_success', labelKey: 'jobPost.options.departments.customerSuccess' },
  { value: 'other', labelKey: 'jobPost.options.other' },
];

export const COUNTRY_OPTIONS: CountryOptionItem[] = [
  { value: 'uae', labelKey: 'jobPost.options.countries.uae', isoCode: 'AE' },
  { value: 'saudi', labelKey: 'jobPost.options.countries.saudi', isoCode: 'SA' },
  { value: 'qatar', labelKey: 'jobPost.options.countries.qatar', isoCode: 'QA' },
  { value: 'oman', labelKey: 'jobPost.options.countries.oman', isoCode: 'OM' },
  { value: 'bahrain', labelKey: 'jobPost.options.countries.bahrain', isoCode: 'BH' },
  { value: 'kuwait', labelKey: 'jobPost.options.countries.kuwait', isoCode: 'KW' },
  { value: 'egypt', labelKey: 'jobPost.options.countries.egypt', isoCode: 'EG' },
  { value: 'jordan', labelKey: 'jobPost.options.countries.jordan', isoCode: 'JO' },
  { value: 'remote', labelKey: 'jobPost.options.countries.remote', isoCode: null },
];

/**
 * Cities, each tagged with the country that owns it.
 *
 * The eleven ids that predate the `country` tag are kept verbatim — stored jobs
 * hold these strings and `lib/company-jobs.ts` resolves labels from them — and
 * the rest mirror `lib/country-data.ts` TOP_CITIES so a country with one city in
 * the old list no longer collapses to a single choice once the picker filters.
 */
export const CITY_OPTIONS: CityOptionItem[] = [
  // United Arab Emirates
  { value: 'dubai', labelKey: 'jobPost.options.cities.dubai', country: 'uae' },
  { value: 'abu_dhabi', labelKey: 'jobPost.options.cities.abuDhabi', country: 'uae' },
  { value: 'sharjah', labelKey: 'jobPost.options.cities.sharjah', country: 'uae' },
  { value: 'ajman', labelKey: 'jobPost.options.cities.ajman', country: 'uae' },
  { value: 'ras_al_khaimah', labelKey: 'jobPost.options.cities.rasAlKhaimah', country: 'uae' },
  { value: 'fujairah', labelKey: 'jobPost.options.cities.fujairah', country: 'uae' },
  { value: 'al_ain', labelKey: 'jobPost.options.cities.alAin', country: 'uae' },
  // Saudi Arabia
  { value: 'riyadh', labelKey: 'jobPost.options.cities.riyadh', country: 'saudi' },
  { value: 'jeddah', labelKey: 'jobPost.options.cities.jeddah', country: 'saudi' },
  { value: 'dammam', labelKey: 'jobPost.options.cities.dammam', country: 'saudi' },
  { value: 'makkah', labelKey: 'jobPost.options.cities.makkah', country: 'saudi' },
  { value: 'madinah', labelKey: 'jobPost.options.cities.madinah', country: 'saudi' },
  { value: 'khobar', labelKey: 'jobPost.options.cities.khobar', country: 'saudi' },
  // Qatar
  { value: 'doha', labelKey: 'jobPost.options.cities.doha', country: 'qatar' },
  { value: 'al_wakrah', labelKey: 'jobPost.options.cities.alWakrah', country: 'qatar' },
  { value: 'al_rayyan', labelKey: 'jobPost.options.cities.alRayyan', country: 'qatar' },
  // Oman
  { value: 'muscat', labelKey: 'jobPost.options.cities.muscat', country: 'oman' },
  { value: 'salalah', labelKey: 'jobPost.options.cities.salalah', country: 'oman' },
  { value: 'sohar', labelKey: 'jobPost.options.cities.sohar', country: 'oman' },
  // Bahrain
  { value: 'manama', labelKey: 'jobPost.options.cities.manama', country: 'bahrain' },
  { value: 'muharraq', labelKey: 'jobPost.options.cities.muharraq', country: 'bahrain' },
  { value: 'riffa', labelKey: 'jobPost.options.cities.riffa', country: 'bahrain' },
  // Kuwait
  { value: 'kuwait_city', labelKey: 'jobPost.options.cities.kuwaitCity', country: 'kuwait' },
  { value: 'hawalli', labelKey: 'jobPost.options.cities.hawalli', country: 'kuwait' },
  { value: 'salmiya', labelKey: 'jobPost.options.cities.salmiya', country: 'kuwait' },
  // Egypt
  { value: 'cairo', labelKey: 'jobPost.options.cities.cairo', country: 'egypt' },
  { value: 'alexandria', labelKey: 'jobPost.options.cities.alexandria', country: 'egypt' },
  { value: 'giza', labelKey: 'jobPost.options.cities.giza', country: 'egypt' },
  { value: 'sharm_el_sheikh', labelKey: 'jobPost.options.cities.sharmElSheikh', country: 'egypt' },
  // Jordan
  { value: 'amman', labelKey: 'jobPost.options.cities.amman', country: 'jordan' },
  { value: 'irbid', labelKey: 'jobPost.options.cities.irbid', country: 'jordan' },
  { value: 'zarqa', labelKey: 'jobPost.options.cities.zarqa', country: 'jordan' },
];

export const WORK_TYPE_OPTIONS: OptionItem[] = [
  { value: 'onsite', labelKey: 'jobPost.options.workType.onsite' },
  { value: 'hybrid', labelKey: 'jobPost.options.workType.hybrid' },
  { value: 'remote', labelKey: 'jobPost.options.workType.remote' },
];

export const EMPLOYMENT_OPTIONS: OptionItem[] = [
  { value: 'full_time', labelKey: 'jobPost.options.employment.fullTime' },
  { value: 'part_time', labelKey: 'jobPost.options.employment.partTime' },
  { value: 'contract', labelKey: 'jobPost.options.employment.contract' },
  { value: 'internship', labelKey: 'jobPost.options.employment.internship' },
];

/** Legacy experience-level ids — still used to label stored jobs. */
export const EXPERIENCE_LEVEL_OPTIONS: OptionItem[] = [
  { value: 'entry_0_2', labelKey: 'jobPost.options.experienceLevel.entry0to2' },
  { value: 'mid_2_5', labelKey: 'jobPost.options.experienceLevel.mid2to5' },
  { value: 'senior_5_8', labelKey: 'jobPost.options.experienceLevel.senior5to8' },
  { value: 'lead_8_12', labelKey: 'jobPost.options.experienceLevel.lead8to12' },
  { value: 'executive_12_plus', labelKey: 'jobPost.options.experienceLevel.executive12plus' },
];

export function getOptionLabel(options: OptionItem[], value: string): string {
  const hit = options.find((option) => option.value === value);
  return hit ? hit.labelKey : '';
}

/**
 * Cities belonging to a country option.
 *
 * An unset country means the employer has not narrowed anything down yet, so
 * the full catalogue stays browsable; "Remote" has no cities of its own and is
 * treated the same way, since a remote role may still name a hub office.
 */
export function getCitiesForCountryOption(country: string): CityOptionItem[] {
  if (!country || country === 'remote') return CITY_OPTIONS;
  const scoped = CITY_OPTIONS.filter((city) => city.country === country);
  return scoped.length > 0 ? scoped : CITY_OPTIONS;
}

/** The country option a city belongs to, or `null` for a free-text city. */
export function getCountryForCityOption(city: string): string | null {
  return CITY_OPTIONS.find((option) => option.value === city)?.country ?? null;
}

/**
 * The economic region a country option sits in, as an i18n key.
 *
 * Candidate location offers a "region" tier so an employer can open a role to
 * the bloc rather than the single country. GCC states share a labour market and
 * mutual work rights, so they resolve to `gcc`; the rest fall back to the wider
 * Middle East. A country with no region — Remote — has no region tier and the
 * step drops that option.
 */
const COUNTRY_REGION_LABEL_KEYS: Record<string, string> = {
  uae: 'location.regions.gcc',
  saudi: 'location.regions.gcc',
  qatar: 'location.regions.gcc',
  oman: 'location.regions.gcc',
  bahrain: 'location.regions.gcc',
  kuwait: 'location.regions.gcc',
  egypt: 'location.regions.middleEast',
  jordan: 'location.regions.middleEast',
};

export function getRegionLabelKeyForCountry(country: string): string | null {
  return COUNTRY_REGION_LABEL_KEYS[country] ?? null;
}

/** True when the city is a known option that does not belong to the country. */
export function isCityOutsideCountry(city: string, country: string): boolean {
  if (!city || !country || country === 'remote') return false;
  const owner = getCountryForCityOption(city);
  return owner !== null && owner !== country;
}
